import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getCalculation,
  fetchHospitalConfig,
  createShareLink,
} from "../lib/api";
import { formatRupiahDot } from "../lib/format";
import LetterheadDoc from "../components/LetterheadDoc";
import {
  Printer,
  ArrowLeft,
  WhatsappLogo,
  CopySimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PrintEstimatePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState("");
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState("");

  useEffect(() => {
    Promise.all([getCalculation(id), fetchHospitalConfig()])
      .then(([d, c]) => {
        setDoc(d);
        setCfg(c);
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Gagal memuat data."));
  }, [id]);

  const buildShare = async () => {
    setSharing(true);
    try {
      const { share_token, ttl_hours } = await createShareLink(id);
      const url = `${window.location.origin}/share/${share_token}`;
      setShareUrl(url);
      return { url, ttl_hours };
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuat link share.");
      return null;
    } finally {
      setSharing(false);
    }
  };

  const handleShareWA = async () => {
    const res = shareUrl
      ? { url: shareUrl, ttl_hours: 48 }
      : await buildShare();
    if (!res) return;
    const msg = `*Estimasi Biaya Operasi*\n${cfg.name}\n\nPasien: ${doc.nama_pasien}\nRuang/Kelas: ${doc.ruang_kelas} (${doc.kelas})\nTotal Perkiraan: ${formatRupiahDot(
      doc.grand_total,
    )}\n\nLihat rincian lengkap:\n${res.url}\n\n_Link berlaku ${res.ttl_hours} jam._`;
    const wa = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(wa, "_blank", "noopener");
  };

  const handleCopyLink = async () => {
    const res = shareUrl ? { url: shareUrl } : await buildShare();
    if (!res) return;
    try {
      await navigator.clipboard.writeText(res.url);
      toast.success("Link disalin ke clipboard.");
    } catch {
      toast.info(res.url);
    }
  };

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        {err}
      </div>
    );
  }
  if (!doc || !cfg) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Memuat...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="no-print bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-[900px] mx-auto px-4 py-3 flex items-center justify-between flex-wrap gap-2">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm text-slate-700 hover:text-[#0A3F9A]"
            data-testid="btn-print-back"
          >
            <ArrowLeft size={16} /> Kembali
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={handleCopyLink}
              disabled={sharing}
              className="bg-white border border-slate-300 text-slate-700 px-3 py-2 rounded hover:bg-slate-50 text-sm font-medium flex items-center gap-2 disabled:opacity-60"
              data-testid="btn-copy-share-link"
            >
              <CopySimple size={14} weight="duotone" /> Salin Link
            </button>
            <button
              onClick={handleShareWA}
              disabled={sharing}
              className="bg-[#25D366] text-white px-4 py-2 rounded hover:bg-[#1ebd5b] text-sm font-medium flex items-center gap-2 disabled:opacity-60"
              data-testid="btn-share-whatsapp"
            >
              <WhatsappLogo size={16} weight="duotone" />
              {sharing ? "Membuat..." : "Share via WhatsApp"}
            </button>
            <button
              onClick={() => window.print()}
              className="bg-[#0A3F9A] text-white px-4 py-2 rounded hover:bg-[#082F73] text-sm font-medium flex items-center gap-2"
              data-testid="btn-print-now"
            >
              <Printer size={16} weight="duotone" /> Print / PDF
            </button>
          </div>
        </div>
        {shareUrl && (
          <div className="max-w-[900px] mx-auto px-4 py-2 text-xs text-slate-600 border-t border-slate-100 break-all">
            <span className="font-medium text-slate-500">Link aktif:</span>{" "}
            <a
              href={shareUrl}
              target="_blank"
              rel="noreferrer"
              className="text-blue-700 hover:underline"
              data-testid="text-share-url"
            >
              {shareUrl}
            </a>
            <span className="ml-2 text-slate-400">(berlaku 48 jam)</span>
          </div>
        )}
      </div>

      <LetterheadDoc doc={doc} cfg={cfg} />
    </div>
  );
}
